// ============================================================
// Lightweight in-memory data cache — 60-second TTL.
//
// Every page's loadData() checks the cache before hitting Supabase.
// If a fresh entry exists (< 60s old) it returns immediately with no
// network call, making tab navigation feel instant. If stale or absent,
// it fetches fresh data, stores it, and returns it.
//
// Cache is keyed by a string per data set (e.g. 'dashboard', 'balance').
// Keys that share data (e.g. 'transactions' used by both Transactions and
// Dashboard) benefit from the same cached result.
//
// Any successful WRITE operation (insert/update/delete) should call
// invalidate(key) or invalidateAll() so the next read triggers a fresh
// fetch — preventing stale data after user actions.
//
// This is a module-level singleton (plain JS object), so it persists
// for the entire browser session without any React context overhead.
// ============================================================

const TTL_MS = 60_000; // 60 seconds

const store = {}; // { [key]: { data, timestamp } }

/**
 * Get cached data for `key` if it exists and is still fresh.
 * Returns null if missing or stale.
 */
export function getCached(key) {
  const entry = store[key];
  if (!entry) return null;
  if (Date.now() - entry.timestamp > TTL_MS) {
    delete store[key];
    return null;
  }
  return entry.data;
}

/**
 * Store `data` in the cache under `key`, timestamped now.
 */
export function setCached(key, data) {
  store[key] = { data, timestamp: Date.now() };
}

/**
 * Invalidate a specific cache key — call this after any write
 * operation affecting that data set so the next read is fresh.
 */
export function invalidate(key) {
  delete store[key];
}

/**
 * Invalidate multiple keys at once. Each entry is treated as a PREFIX —
 * any stored key that STARTS WITH that string is invalidated. This lets
 * callers invalidate e.g. all dashboard variants with just 'dashboard'.
 */
export function invalidateKeys(prefixes) {
  const allKeys = Object.keys(store);
  prefixes.forEach(prefix => {
    allKeys.forEach(k => { if (k.startsWith(prefix)) delete store[k]; });
  });
}

/**
 * Nuke the entire cache — call on logout or after bulk imports.
 * Also clears the persistent IndexedDB cache.
 */
export function invalidateAll() {
  Object.keys(store).forEach(k => delete store[k]);
  // Also clear persistent cache asynchronously
  import('./persistentCache').then(m => m.cacheInvalidateAll()).catch(() => {});
}

/**
 * Convenience helper: if fresh data exists in cache, return it.
 * Otherwise call `fetchFn`, cache the result, and return it.
 *
 * Usage:
 *   const data = await withCache('dashboard', async () => {
 *     const { data } = await supabase.from('transactions').select('*');
 *     return data;
 *   });
 */
export async function withCache(key, fetchFn) {
  const cached = getCached(key);
  if (cached !== null) return cached;
  const fresh = await fetchFn();
  setCached(key, fresh);
  return fresh;
}
